import { useState, useEffect, useCallback, useMemo } from "react";
import DashboardLayout from "../layouts/DashboardLayout";
import api from "../api";
import { toast } from "react-toastify";
import { Building, Plus, CreditCard, X, AlertTriangle, Search, History, User, MoreVertical } from "lucide-react";
import { createPortal } from "react-dom";

export default function OrganizationCredits() {
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  
  // Search
  const [searchTerm, setSearchTerm] = useState("");

  // Add Credits Modal
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [addAmount, setAddAmount] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // History Modal
  const [historyEntity, setHistoryEntity] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Row actions menu - same 3-dot/portal/fixed-position pattern as
  // pages/actors/doctor/Doctor.js, used consistently across table pages
  // instead of multiple inline action buttons per row.
  const [menuOpen, setMenuOpen] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });

  const handleMenuToggle = (event, entityKey) => {
    if (menuOpen === entityKey) {
      setMenuOpen(null);
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const right = Math.max(16, window.innerWidth - rect.right);
    const top = rect.bottom + 4;
    const estimatedHeight = 100;
    const adjustedTop = (top + estimatedHeight > window.innerHeight && rect.top > estimatedHeight)
      ? rect.top - estimatedHeight - 4
      : top;
    setMenuPosition({ top: adjustedTop, right });
    setMenuOpen(entityKey);
  };

  const fetchEntities = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getAdminCreditEntities();
      setEntities(res.data || []);
    } catch (err) {
      if (err.response?.status === 403 || err.response?.status === 401) {
        setAccessDenied(true);
      } else {
        toast.error("Failed to load entities");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEntities();
  }, [fetchEntities]);

  const handleAddCredits = async () => {
    const amount = parseInt(addAmount, 10);
    if (!amount || amount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    setIsSubmitting(true);
    try {
      await api.addCreditsToEntity(selectedEntity.entity_type, selectedEntity.id, amount);
      toast.success(`Successfully added ${amount} credits to ${selectedEntity.name}`);
      setAddAmount("");
      setSelectedEntity(null);
      fetchEntities(); // Refresh list
    } catch (err) {
      toast.error("Failed to add credits");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleViewHistory = async (entity) => {
    setHistoryEntity(entity);
    setHistoryLoading(true);
    try {
      const res = await api.getAdminEntityCreditHistory(entity.entity_type, entity.id);
      setHistoryData(res.data?.credit_history || []);
    } catch (err) {
      toast.error("Failed to load credit history");
      setHistoryEntity(null);
    } finally {
      setHistoryLoading(false);
    }
  };

  const filteredEntities = useMemo(() => {
    if (!searchTerm) return entities;
    const lower = searchTerm.toLowerCase();
    return entities.filter(e => 
      e.name.toLowerCase().includes(lower) || 
      String(e.id).includes(lower) || 
      (e.uid && e.uid.toLowerCase().includes(lower))
    );
  }, [entities, searchTerm]);

  if (accessDenied) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center h-96 gap-4 text-center">
          <AlertTriangle size={48} className="text-rose-400" />
          <h2 className="text-xl font-bold text-secondary-800">Access Restricted</h2>
          <p className="text-secondary-500 max-w-md">
            Credit management is only accessible to users belonging to the admin organisation.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-primary-100 flex items-center justify-center">
              <CreditCard size={20} className="text-primary-500" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-secondary-800">Entity Credits</h1>
              <p className="text-xs text-secondary-400">View and manage credits for all organizations and individuals</p>
            </div>
          </div>
          
          <div className="relative w-full md:w-64">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search size={16} className="text-secondary-400" />
            </div>
            <input
              type="text"
              placeholder="Search by name, ID or UID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white border border-secondary-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 transition-all shadow-sm"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white/70 backdrop-blur-sm border border-white/20 shadow-soft rounded-2xl overflow-hidden">
          {loading ? (
            <div className="p-8 space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-10 bg-secondary-100 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : filteredEntities.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3 text-secondary-400">
              <Building size={36} />
              <p className="text-sm">No entities found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-secondary-100 bg-secondary-50/50">
                    <th className="px-4 py-3 text-left font-semibold text-secondary-600">ID</th>
                    <th className="px-4 py-3 text-left font-semibold text-secondary-600">Type</th>
                    <th className="px-4 py-3 text-left font-semibold text-secondary-600">Name</th>
                    <th className="px-4 py-3 text-left font-semibold text-secondary-600">UID / Email</th>
                    <th className="px-4 py-3 text-left font-semibold text-secondary-600">Available Credits</th>
                    <th className="px-4 py-3 text-left font-semibold text-secondary-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEntities.map((entity) => (
                    <tr key={`${entity.entity_type}-${entity.id}`} className="border-b border-secondary-50 hover:bg-primary-50/40 transition-colors">
                      <td className="px-4 py-3 text-secondary-500 font-mono text-xs">{entity.id}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-semibold ${
                          entity.entity_type_name === 'Organization' 
                            ? 'bg-blue-100 text-blue-700' 
                            : 'bg-purple-100 text-purple-700'
                        }`}>
                          {entity.entity_type_name === 'Organization' ? <Building size={12} /> : <User size={12} />}
                          {entity.entity_type_name}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-medium text-secondary-800">{entity.name}</td>
                      <td className="px-4 py-3 text-secondary-500 text-xs font-mono">{entity.uid || "—"}</td>
                      <td className="px-4 py-3">
                        <span className="inline-block px-2 py-1 rounded-lg bg-emerald-100 text-emerald-700 font-bold text-sm">
                          {entity.available_credit}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right relative">
                        <button
                          className="p-1.5 rounded-lg hover:bg-secondary-100 text-secondary-500 hover:text-secondary-800 transition-colors cursor-pointer"
                          onClick={(e) => handleMenuToggle(e, `${entity.entity_type}-${entity.id}`)}
                          data-label="Entity Options"
                          title="Actions"
                        >
                          <MoreVertical size={18} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Row actions menu - portal + fixed position, same pattern as Doctor.js */}
      {menuOpen && (() => {
        const selectedRowEntity = entities.find(e => `${e.entity_type}-${e.id}` === menuOpen);
        if (!selectedRowEntity) return null;
        return createPortal(
          <div
            className="fixed bg-white border border-secondary-200 rounded-xl shadow-xl z-50 min-w-[160px] py-1.5"
            style={{ top: `${menuPosition.top}px`, right: `${menuPosition.right}px` }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="flex items-center gap-2 w-full text-left px-4 py-2 hover:bg-secondary-50 transition-colors text-sm font-medium text-secondary-800"
              onClick={() => { setMenuOpen(null); setSelectedEntity(selectedRowEntity); }}
              data-label="Add Credits"
            >
              <Plus size={14} /> Add Credits
            </button>
            <button
              className="flex items-center gap-2 w-full text-left px-4 py-2 hover:bg-secondary-50 transition-colors text-sm font-medium text-secondary-800"
              onClick={() => { setMenuOpen(null); handleViewHistory(selectedRowEntity); }}
              data-label="View History"
            >
              <History size={14} /> View History
            </button>
          </div>,
          document.body
        );
      })()}

      {/* Click outside to close menu */}
      {menuOpen && (
        <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(null)} />
      )}

      {/* Add Credits Modal */}
      {selectedEntity && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={() => setSelectedEntity(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold text-secondary-800">Add Credits</h2>
              <button onClick={() => setSelectedEntity(null)} className="text-secondary-400 hover:text-secondary-600">
                <X size={20} />
              </button>
            </div>
            
            <p className="text-sm text-secondary-600 mb-4">
              Adding credits to <strong className="text-secondary-800">{selectedEntity.name}</strong> ({selectedEntity.entity_type_name}).
            </p>

            <div className="space-y-2 mb-6">
              <label className="text-xs font-semibold text-secondary-600 uppercase">Amount</label>
              <input
                type="number"
                min="1"
                value={addAmount}
                onChange={(e) => setAddAmount(e.target.value)}
                placeholder="Enter amount..."
                className="w-full px-4 py-2 rounded-xl border border-secondary-200 focus:outline-none focus:ring-2 focus:ring-primary-300"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setSelectedEntity(null)}
                className="flex-1 py-2 rounded-xl border border-secondary-200 text-secondary-600 font-medium hover:bg-secondary-50 transition-colors"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                onClick={handleAddCredits}
                className="flex-1 py-2 rounded-xl bg-primary-500 text-white font-medium hover:bg-primary-600 transition-colors disabled:opacity-50"
                disabled={isSubmitting || !addAmount}
              >
                {isSubmitting ? "Adding..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      {historyEntity && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={() => setHistoryEntity(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center p-5 border-b border-secondary-100">
              <div>
                <h2 className="text-lg font-bold text-secondary-800">Credit History</h2>
                <p className="text-xs text-secondary-500">{historyEntity.name} ({historyEntity.entity_type_name})</p>
              </div>
              <button onClick={() => setHistoryEntity(null)} className="text-secondary-400 hover:text-secondary-600 p-1 rounded-lg hover:bg-secondary-50 transition-colors">
                <X size={20} />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-5">
              {historyLoading ? (
                <div className="space-y-3">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="h-16 bg-secondary-50 rounded-xl animate-pulse" />
                  ))}
                </div>
              ) : historyData.length === 0 ? (
                <div className="text-center py-10 text-secondary-400">
                  <History size={32} className="mx-auto mb-3 opacity-50" />
                  <p>No credit history found for this entity.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {historyData.map((h) => {
                    const isCredit = h.transaction_type === "CREDIT";
                    // If it's a CREDIT to the selected entity, or DEBIT from it, we format appropriately.
                    // But here we'll just display what the record says.
                    const isGain = (h.destination_id === historyEntity.id && h.destination_type === historyEntity.entity_type) && isCredit;
                    
                    return (
                      <div key={h.id} className="flex items-center justify-between p-3 rounded-xl border border-secondary-100 bg-white shadow-sm">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold tracking-wide uppercase ${
                              isCredit ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
                            }`}>
                              {h.transaction_type}
                            </span>
                            <span className="text-xs text-secondary-400 font-mono">
                              {new Date(h.created_at).toLocaleString()}
                            </span>
                          </div>
                          <div className="text-sm text-secondary-600">
                            From: <span className="font-semibold text-secondary-800">{h.source_name || `ID ${h.source_id}`}</span> 
                            {" "}→ To: <span className="font-semibold text-secondary-800">{h.destination_name || `ID ${h.destination_id}`}</span>
                          </div>
                        </div>
                        <div className={`font-bold text-lg ${isCredit ? 'text-emerald-600' : 'text-rose-600'}`}>
                          {isCredit ? '+' : '-'}{h.total_credit}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            
            <div className="p-4 border-t border-secondary-100 bg-secondary-50 flex justify-end">
              <button
                onClick={() => setHistoryEntity(null)}
                className="px-4 py-2 rounded-xl bg-white border border-secondary-200 text-secondary-600 font-medium hover:bg-secondary-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
